//Built-in libraries

//Third party libraries
import {
  ArchiveIcon,
  CheckCircleIcon,
  PencilAltIcon,
  ReplyIcon,
} from "@heroicons/react/solid";
import { useDispatch, useSelector } from "react-redux";

//Custom components

//Services
import { workspaceSelector } from "../../store";
import { setCurrentWorkspace } from "../../store/Workspace";

//Assets andd Miscellaneous
import { IWorkspace } from "../../types/Workspace";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  patchArchiveWorkspace,
  patchUnArchiveWorkspace,
} from "api/Terminal/Workspace";
import { notifyUser } from "store/Toast";
import { sanitizeResponseError } from "utils";
import { ReactQueryKey } from "constants/ReactQueryKeys";
import { useState } from "react";
import Confirm from "components/UI/Modal/Confirm";
import { HasPermission } from "components/Permission/HasPermission";

//Component Begin
export interface IWorkspaceItemProps {
  onEdit: () => void;
  workspace: IWorkspace;
}
export const WorkspaceItem = ({ workspace, onEdit }: IWorkspaceItemProps) => {
  const dispatch = useDispatch();
  const { currentWorkspace } = useSelector(workspaceSelector);
  const queryClient = useQueryClient();
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const archiveWorkspace = useMutation(
    () => patchArchiveWorkspace({ id: Number(workspace.id) }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ReactQueryKey.GET_WORKSPACES]);

        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Program Archive successful`,
            toastType: "success",
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: "failed",
          })
        );
      },
    }
  );

  const unarchiveWorkspace = useMutation(
    () => patchUnArchiveWorkspace({ id: Number(workspace.id) }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ReactQueryKey.GET_WORKSPACES]);

        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Program Unarchive successful`,
            toastType: "success",
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: "failed",
          })
        );
      },
    }
  );

  const archiveProgramHandler = () => {
    setShowArchiveConfirm(true);
  };
  const confirmArchiveProgram = async () => {
    setShowArchiveConfirm(false);
    if (workspace.archived) {
      unarchiveWorkspace.mutate();
    } else {
      archiveWorkspace.mutate();
    }
  };

  const cancelArchiveProgram = () => {
    setShowArchiveConfirm(false);
  };

  return (
    <>
      {showArchiveConfirm && (
        <Confirm
          title={`${workspace.archived ? "Unarchive" : "Archive"} Program`}
          message={`You are about to ${
            workspace.archived ? "unarchive" : "archive"
          } ${workspace.name} program. Are you sure you want to ${
            workspace.archived ? "unarchive" : "archive"
          } ?`}
          acceptButtonLabel={`${workspace.archived ? "Unarchive" : "Archive"}`}
          declineButtonLabel="Cancel"
          accept={confirmArchiveProgram}
          decline={cancelArchiveProgram}
        />
      )}
      <div
        className="grid grid-cols-6 items-center gap-4 cursor-pointer hover:bg-neutralColor-lighter px-5 py-2 relative"
        role="button"
        onClick={() => dispatch(setCurrentWorkspace(workspace))}
        id={workspace?.id?.toString()}
      >
        <div className="flex flex-col col-span-5">
          <div className="flex items-center gap-1">
            <p className="truncate font-semibold text-sm">{workspace?.name}</p>
            {workspace?.id === currentWorkspace?.id && (
              <CheckCircleIcon className="h-5 w-5 text-primaryColor-normal absolute left-0 " />
            )}
          </div>
          <span className="flex-shrink font-light text-sm">
            {workspace.description}
          </span>
        </div>
        {workspace.name !== "All Program" && (
          <div className="flex justify-end gap-2">
            <PencilAltIcon
              className="w-5 h-5 text-neutralColor-light hover:text-primaryColor-normal"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            />
            <HasPermission of="MANAGE_WORKSPACE">
              {!workspace.archived && (
                <ArchiveIcon
                  className="w-5 h-5 text-neutralColor-light hover:text-tertiaryColor-normal"
                  onClick={(e) => {
                    e.stopPropagation();
                    archiveProgramHandler();
                  }}
                />
              )}
              {workspace.archived && (
                <ReplyIcon
                  className="w-5 h-5 text-neutralColor-light hover:text-tertiaryColor-normal"
                  onClick={(e) => {
                    e.stopPropagation();
                    archiveProgramHandler();
                  }}
                />
              )}
            </HasPermission>
          </div>
        )}
      </div>
    </>
  );
};
